import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BellOff, Loader2, Sparkles } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Badge } from '../../lib/shadcn/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { useGetReorderReminders, useGenerateEmailContent } from '../../hooks/backend/crm'
import { useUpdateCustomer } from '../../hooks/backend/customersPage'
import type { MessageTarget } from './MessageDialog'
import { HelpText } from '../../components/HelpText'

export interface ReorderReminder {
  customer_id: string
  name: string
  first_name: string
  email: string
  phone: string
  order_count: number
  /** Null for customers who have ordered only once. */
  usual_gap_days: number | null
  last_order_date: string
  days_since_last_order: number
  days_overdue: number
  status: 'overdue' | 'lapsed' | 'one_time'
  usual_items: string[]
  last_emailed_at: string | null
}

type Filter = 'overdue' | 'lapsed' | 'one_time'

const FILTERS: Array<{ key: Filter; label: string; empty: string }> = [
  { key: 'overdue', label: 'Overdue', empty: 'No customers are overdue right now.' },
  { key: 'lapsed', label: 'Lapsed', empty: 'No lapsed customers.' },
  { key: 'one_time', label: 'Ordered once', empty: 'No one-time customers past 30 days.' },
]

interface Props {
  /** Opens the message dialog, prefilled with the AI draft. */
  onMessage: (target: MessageTarget) => void
  /** Bumped by the parent to reload (e.g. after a message is sent). */
  refreshKey: number
}

function formatPace(days: number): string {
  if (days >= 14 && Math.abs(days / 7 - Math.round(days / 7)) <= 0.25) {
    return `${Math.round(days / 7)} weeks`
  }
  return `${days} days`
}

function formatSince(days: number): string {
  if (days >= 14) return `${Math.round(days / 7)} weeks`
  return `${days} days`
}

function formatDate(value: string): string {
  const d = new Date(`${value.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function daysAgo(value: string): number {
  return Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000)
}

function buildPrompt(r: ReorderReminder): string {
  const items = r.usual_items.length > 0 ? r.usual_items.join(', ') : ''
  if (r.usual_gap_days === null) {
    return [
      `A friendly follow-up to a wholesale store that placed its first order with us on ${formatDate(r.last_order_date)}, about ${formatSince(r.days_since_last_order)} ago.`,
      items ? `They ordered: ${items}.` : '',
      'Thank them for carrying our products, ask how they have been selling, and invite them to reorder or try something new.',
      "Keep it short and personal. Don't mention that we track their ordering or quote day counts.",
    ]
      .filter(Boolean)
      .join(' ')
  }
  if (r.status === 'lapsed') {
    return [
      `A warm check-in to a wholesale store that carries our products and that we haven't heard from in a while (last order ${formatDate(r.last_order_date)}).`,
      `They used to order about every ${formatPace(r.usual_gap_days)}.`,
      items ? `They usually ordered: ${items}.` : '',
      "See how things are going, say we'd love to get them restocked, and make it easy to reply with an order.",
      "Keep it short and personal. Don't make them feel guilty, and don't mention that we track their ordering or quote day counts.",
    ]
      .filter(Boolean)
      .join(' ')
  }
  return [
    'A friendly reorder check-in to a wholesale store that carries our products.',
    `They usually restock about every ${formatPace(r.usual_gap_days)}, and their last order was about ${formatSince(r.days_since_last_order)} ago (${formatDate(r.last_order_date)}).`,
    items ? `Their usual items: ${items}.` : '',
    "Ask whether they're running low and would like to place an order, and offer to get one started for them.",
    "Keep it short and personal. Don't mention that we track their ordering schedule or quote exact day counts.",
  ]
    .filter(Boolean)
    .join(' ')
}

export default function ReorderRemindersPanel({ onMessage, refreshKey }: Props) {
  const navigate = useNavigate()
  const getReminders = useGetReorderReminders()
  const generate = useGenerateEmailContent()
  const [filter, setFilter] = useState<Filter>('overdue')
  const [draftingId, setDraftingId] = useState<string | null>(null)
  const [skippingId, setSkippingId] = useState<string | null>(null)
  const updateCustomer = useUpdateCustomer()

  useEffect(() => {
    getReminders.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey])

  const reminders = useMemo<ReorderReminder[]>(
    () => (getReminders.data as ReorderReminder[] | undefined) ?? [],
    [getReminders.data]
  )
  const counts = useMemo(
    () => ({
      overdue: reminders.filter(r => r.status === 'overdue').length,
      lapsed: reminders.filter(r => r.status === 'lapsed').length,
      one_time: reminders.filter(r => r.status === 'one_time').length,
    }),
    [reminders]
  )
  const visible = reminders.filter(r => r.status === filter)

  const handleDraft = async (r: ReorderReminder) => {
    setDraftingId(r.customer_id)
    try {
      const first = r.first_name.trim()
      const draft = (await generate.trigger({
        prompt: buildPrompt(r),
        tone: 'Friendly',
        recipient_name: first && first.toLowerCase() !== r.name.toLowerCase() ? `${first} at ${r.name}` : r.name,
      })) as { subject: string; body: string }
      onMessage({ customerId: r.customer_id, name: r.name, email: r.email, phone: r.phone, draft })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to draft the email')
    } finally {
      setDraftingId(null)
    }
  }

  const handleSkip = async (r: ReorderReminder) => {
    setSkippingId(r.customer_id)
    try {
      await updateCustomer.trigger({ customer_id: r.customer_id, reorder_reminders: false })
      toast.success(`${r.name} won't get reorder reminders. Turn it back on from Edit on the Customers page.`)
      getReminders.trigger({}, { skipCache: true })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update the customer')
    } finally {
      setSkippingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground"><HelpText>
        Wholesale customers who are past their usual ordering pace. Overdue means it&apos;s been at least 1.5× their
        usual time between orders; lapsed means 4× or more. Ordered once lists customers with a single order more than
        30 days ago. Draft email writes a reminder in your voice for you to
        review before sending. Customers with an open order (not yet packed or sent), inactive customers, and customers
        you leave out of reminders (the bell button here, or Edit on the Customers page) don&apos;t show up.
      </HelpText></p>

      <div className="grid grid-cols-3 gap-3 max-w-xl">
        {FILTERS.map(({ key: f, label }) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'rounded-lg border p-3 text-left transition-colors',
              filter === f ? 'border-primary bg-accent' : 'border-border hover:bg-accent/50'
            )}
          >
            <div className="text-xs font-medium text-muted-foreground">{label}</div>
            <div className="text-2xl font-bold text-foreground tabular-nums">{counts[f]}</div>
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Usually orders</TableHead>
              <TableHead>Last order</TableHead>
              <TableHead>Last emailed</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {getReminders.loading && reminders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                  {FILTERS.find(f => f.key === filter)?.empty}
                </TableCell>
              </TableRow>
            ) : (
              visible.map(r => {
                const emailedSinceOrder =
                  r.last_emailed_at !== null && r.last_emailed_at.slice(0, 10) >= r.last_order_date
                return (
                  <TableRow key={r.customer_id}>
                    <TableCell className="max-w-[260px]">
                      <button
                        className="font-medium text-primary hover:underline text-left"
                        onClick={() => navigate(`/customers/${encodeURIComponent(r.customer_id)}`)}
                      >
                        {r.name}
                      </button>
                      {r.usual_items.length > 0 && (
                        <p className="text-xs text-muted-foreground truncate" title={r.usual_items.join(', ')}>
                          {r.usual_items.join(', ')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.usual_gap_days === null ? (
                        'Ordered once'
                      ) : (
                        <>
                          Every {formatPace(r.usual_gap_days)}
                          <div className="text-xs text-muted-foreground">{r.order_count} orders</div>
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(r.last_order_date)}
                      <div className="text-xs text-muted-foreground">
                        {r.days_since_last_order} days ago
                        {r.usual_gap_days !== null && (
                          <>
                            ,{' '}
                            <span className="text-amber-700 dark:text-amber-300">{r.days_overdue} days late</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.last_emailed_at ? (
                        <>
                          {daysAgo(r.last_emailed_at) === 0 ? 'Today' : `${daysAgo(r.last_emailed_at)} days ago`}
                          {emailedSinceOrder && (
                            <Badge variant="secondary" className="ml-1.5 text-xs">Reminded</Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="mr-1 text-muted-foreground"
                        aria-label={`Leave ${r.name} out of reorder reminders`}
                        title="Leave out of reorder reminders"
                        disabled={skippingId !== null}
                        onClick={() => handleSkip(r)}
                      >
                        {skippingId === r.customer_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <BellOff className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={draftingId !== null || !r.email}
                        title={r.email ? undefined : 'No email on file'}
                        onClick={() => handleDraft(r)}
                      >
                        {draftingId === r.customer_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        {draftingId === r.customer_id ? 'Drafting…' : r.email ? 'Draft email' : 'No email'}
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
